import { ReactNode, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useBarcodeRouter, ScanRouteResult } from '../hooks/useBarcodeRouter';
import clsx from 'clsx';
import SystemBanner from './SystemBanner';

interface LayoutProps {
  children: ReactNode;
}

// Icons
const HamburgerIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// SVG Icons for navigation
const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const QueueIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TimeOffIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrainingIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const DocumentsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const RecordsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const RewardsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const ComingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
  </svg>
);

const HistoryIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ProfileIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const BarcodeIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2v16H3V4zm4 0h1v16H7V4zm3 0h2v16h-2V4zm4 0h3v16h-3V4zm5 0h2v16h-2V4z" />
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const ScanIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2v16H3V4zm4 0h1v16H7V4zm3 0h2v16h-2V4zm4 0h3v16h-3V4zm5 0h2v16h-2V4z" />
  </svg>
);

const DropsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const InventoryIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  requiresPermission?: string;
}

const OrdersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const ChevronDownIcon = ({ open }: { open: boolean }) => (
  <svg className={clsx('w-4 h-4 transition-transform', open && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// Standalone top nav items (no group header)
const SpecialOrderIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const topNavItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/production-calendar', label: 'Production Calendar', icon: <CalendarIcon /> },
  { to: '/queue', label: 'My Queue', icon: <QueueIcon /> },
  { to: '/orders', label: 'Orders', icon: <OrdersIcon /> },
  { to: '/special-orders', label: 'Special Orders', icon: <SpecialOrderIcon /> },
  { to: '/scan', label: 'Scan', icon: <ScanIcon /> },
  { to: '/drops', label: 'Drops', icon: <DropsIcon /> },
  { to: '/inventory', label: 'Inventory', icon: <InventoryIcon />, requiresPermission: 'can_add_inventory' },
];

// Collapsible groups
interface NavGroup {
  key: string;
  label: string;
  icon: ReactNode;
  items: NavItem[];
}

const SupportIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const navGroups: NavGroup[] = [
  {
    key: 'time',
    label: 'TIME & ATTENDANCE',
    icon: <ClockIcon />,
    items: [
      { to: '/time-clock', label: 'Time Clock', icon: <ClockIcon /> },
      { to: '/time-off', label: 'Time Off', icon: <TimeOffIcon /> },
      { to: '/schedule', label: 'Company Calendar', icon: <CalendarIcon /> },
    ],
  },
  {
    key: 'training',
    label: 'TRAINING & DOCS',
    icon: <TrainingIcon />,
    items: [
      { to: '/training', label: 'Training', icon: <TrainingIcon /> },
      { to: '/documents', label: 'Documents', icon: <DocumentsIcon /> },
    ],
  },
  {
    key: 'planning',
    label: 'PLANNING',
    icon: <ComingUpIcon />,
    items: [
      { to: '/coming-up', label: 'Coming Up', icon: <ComingUpIcon /> },
      { to: '/history', label: 'History', icon: <HistoryIcon /> },
    ],
  },
  {
    key: 'support',
    label: 'SUPPORT',
    icon: <SupportIcon />,
    items: [
      { to: '/report-problem', label: 'Report a Problem', icon: <SupportIcon /> },
      { to: '/my-requests', label: 'My Requests', icon: <RecordsIcon /> },
    ],
  },
];

// Bottom standalone items
const bottomNavItems: NavItem[] = [
  // Records to review: acknowledging is the employee's choice, never a gate.
  { to: '/records', label: 'Records to review', icon: <RecordsIcon /> },
  { to: '/my-reviews', label: 'My Reviews', icon: <RecordsIcon /> },
  { to: '/rewards', label: 'Rewards', icon: <RewardsIcon /> },
  { to: '/profile', label: 'Profile', icon: <ProfileIcon /> },
];

const filterByPermission = (items: NavItem[], user: any): NavItem[] => {
  const permissions = user?.permissions || {};
  return items.filter(item => {
    if (!item.requiresPermission) return true;
    if (permissions.all_access === true) return true;
    return permissions[item.requiresPermission] === true;
  });
};

const COLLAPSE_STORAGE_KEY = 'ws-nav-collapsed';

function loadCollapseState(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  // Default: all collapsed
  return { time: true, training: true, planning: true };
}

function saveCollapseState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function NavLink({ to, active, icon, children, onClick, badge }: { to: string; active: boolean; icon?: ReactNode; children: ReactNode; onClick?: () => void; badge?: number }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={clsx(
        'block px-4 py-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] flex items-center gap-3',
        active
          ? 'bg-yellow-400 text-gray-900'
          : 'text-gray-300 hover:bg-gray-700'
      )}
    >
      {icon && <span className="w-5 h-5 flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-auto px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white min-w-[20px] text-center animate-pulse">
          {badge}
        </span>
      )}
    </Link>
  );
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scanToast, setScanToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapseState);
  const [specialOrderCount, setSpecialOrderCount] = useState(0);
  // Count only — safe on a shared screen. Content needs a password re-entry.
  const [recordsCount, setRecordsCount] = useState(0);
  // Flasher: the employee's own support requests that were responded to since
  // they last opened My Requests.
  const [myRequestsUnseen, setMyRequestsUnseen] = useState(0);
  const [myReviewsPending, setMyReviewsPending] = useState(0);

  useEffect(() => {
    const fetchRecordsCount = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/discipline/my-records/count', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) setRecordsCount((await res.json()).total || 0);
      } catch {}
    };
    const fetchMyRequestsUnseen = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/support-requests/mine/unseen-count', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) setMyRequestsUnseen((await res.json()).count || 0);
      } catch {}
    };
    const fetchMyReviewsPending = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await fetch("/api/reviews/mine/pending-count", { headers: { "Authorization": `Bearer ${token}` } });
        if (res.ok) setMyReviewsPending((await res.json()).count || 0);
      } catch {}
    };
    fetchRecordsCount();
    fetchMyRequestsUnseen();
    fetchMyReviewsPending();
    const interval = setInterval(() => { fetchRecordsCount(); fetchMyRequestsUnseen(); fetchMyReviewsPending(); }, 60000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/orders/special-list', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const active = (data || []).filter((o: any) => !['shipped', 'completed', 'cancelled', 'closed', 'delivered'].includes(o.status));
          setSpecialOrderCount(active.length);
        }
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapseState(next);
      return next;
    });
  }, []);

  // Barcode scanner integration
  const { routeBarcode } = useBarcodeRouter();
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    const result: ScanRouteResult = await routeBarcode(barcode);
    if (result.navigated) {
      setScanToast({ message: result.message, type: 'success' });
    } else {
      setScanToast({ message: result.message, type: 'error' });
    }
  }, [routeBarcode]);
  const { isScanning, inputRef } = useBarcodeScanner(handleBarcodeScan);

  // Auto-dismiss scan toast
  useEffect(() => {
    if (scanToast) {
      const timer = setTimeout(() => setScanToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [scanToast]);

  // Close mobile menu and scroll to top on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col md:flex-row">

      {/* Mobile Header - Large User Display */}
      <header className="md:hidden border-b sticky top-0 z-30" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
        {/* Top bar with menu and actions */}
        <div className="h-12 flex items-center justify-between px-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-primary)' }}
            aria-label="Open menu"
          >
            <HamburgerIcon />
          </button>
          <div className="flex items-center gap-2">
            <Link
              to="/scan"
              className="p-2 rounded-lg bg-yellow-400 text-gray-900 hover:bg-yellow-300 relative"
              aria-label="Scan barcode"
            >
              <BarcodeIcon />
            </Link>
            <ThemeToggle defaultDark={true} />
            <button
              onClick={logout}
              className="p-2 rounded-lg text-red-400 hover:bg-red-500/20"
              aria-label="Logout"
            >
              <LogoutIcon />
            </button>
          </div>
        </div>
        {/* User Display Banner - Large and visible */}
        <div className="px-3 pb-2 bg-gradient-to-r from-yellow-500 to-yellow-400">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-bold text-lg text-yellow-400">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-bold text-lg truncate">{user?.name || 'Unknown User'}</div>
              <div className="text-gray-800 text-sm font-medium">
                {(user as any)?.jobTitle || ((user as any)?.permissions?.all_access ? 'All Stations' : 'Employee')}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div
        className={clsx('sidebar-overlay md:hidden', mobileMenuOpen && 'open')}
        onClick={closeMobileMenu}
      />

      {/* Mobile Sidebar */}
      <aside
        className={clsx(
          'mobile-sidebar md:hidden bg-gray-800 flex flex-col',
          mobileMenuOpen && 'open'
        )}
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <span className="text-white font-semibold">Menu</span>
          <button
            onClick={closeMobileMenu}
            className="p-2 rounded-lg hover:bg-gray-700 transition-colors text-white"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-700">
          <div className="text-sm text-gray-300">{user?.name}</div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Top standalone items */}
          {filterByPermission(topNavItems, user).map((item) => (
            <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} onClick={closeMobileMenu} badge={item.to === '/special-orders' ? specialOrderCount : undefined}>
              {item.label}
            </NavLink>
          ))}

          {/* Collapsible groups */}
          {navGroups.map((group) => {
            const items = filterByPermission(group.items, user);
            if (items.length === 0) return null;
            const isOpen = !collapsed[group.key];
            return (
              <div key={group.key} className="pt-2">
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 transition-colors min-h-[44px]"
                >
                  <span className="flex items-center gap-3">{group.icon}{group.label}</span>
                  <ChevronDownIcon open={isOpen} />
                </button>
                {isOpen && items.map((item) => (
                  <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} onClick={closeMobileMenu} badge={item.to === '/my-requests' ? myRequestsUnseen : undefined}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {/* Bottom standalone items */}
          <div className="pt-2 border-t border-gray-700 mt-2">
            {filterByPermission(bottomNavItems, user).map((item) => (
              <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} onClick={closeMobileMenu} badge={item.to === '/records' ? recordsCount : item.to === '/my-reviews' ? myReviewsPending : undefined}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={logout}
            className="w-full px-4 py-3 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 text-sm font-medium flex items-center justify-center gap-2 min-h-[44px]"
          >
            <LogoutIcon />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 border-r flex-col flex-shrink-0" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
        {/* Logo and Actions */}
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
          <img
            src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
            alt="Keder"
            className="h-8 w-auto"
          />
          <div className="flex items-center gap-1">
            <Link
              to="/scan"
              className="p-2 rounded-lg bg-yellow-400 text-gray-900 hover:bg-yellow-300 relative"
              aria-label="Scan barcode"
            >
              <BarcodeIcon />
            </Link>
            <ThemeToggle defaultDark={true} />
          </div>
        </div>
        {/* User Display - Prominent */}
        <div className="p-4 bg-gradient-to-r from-yellow-500 to-yellow-400">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center font-bold text-xl text-yellow-400">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-bold text-lg truncate">{user?.name || 'Unknown'}</div>
              <div className="text-gray-800 text-sm font-medium truncate">
                {(user as any)?.jobTitle || ((user as any)?.permissions?.all_access ? 'All Stations' : 'Employee')}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Top standalone items */}
          {filterByPermission(topNavItems, user).map((item) => (
            <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} badge={item.to === '/special-orders' ? specialOrderCount : undefined}>
              {item.label}
            </NavLink>
          ))}

          {/* Collapsible groups */}
          {navGroups.map((group) => {
            const items = filterByPermission(group.items, user);
            if (items.length === 0) return null;
            const isOpen = !collapsed[group.key];
            return (
              <div key={group.key} className="pt-2">
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 transition-colors min-h-[44px]"
                >
                  <span className="flex items-center gap-3">{group.icon}{group.label}</span>
                  <ChevronDownIcon open={isOpen} />
                </button>
                {isOpen && items.map((item) => (
                  <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} badge={item.to === '/my-requests' ? myRequestsUnseen : undefined}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {/* Bottom standalone items */}
          <div className="pt-2 border-t border-gray-700 mt-2">
            {filterByPermission(bottomNavItems, user).map((item) => (
              <NavLink key={item.to} to={item.to} active={location.pathname === item.to} icon={item.icon} badge={item.to === '/records' ? recordsCount : item.to === '/my-reviews' ? myReviewsPending : undefined}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={logout}
            className="w-full px-4 py-3 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 text-sm font-medium flex items-center justify-center gap-2 min-h-[44px]"
          >
            <LogoutIcon />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-16 flex flex-col">
        <SystemBanner />
        <div className="flex-1 p-4 md:p-6">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-6 border-t border-gray-700 text-center text-xs text-gray-500">
        &copy; {new Date().getFullYear()} Keder Screens | <a href="https://kederscreens.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400">Terms</a> | <a href="https://kederscreens.com/privacy/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400">Privacy</a> | <a href="https://kederscreens.com/cookie-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400">Cookies</a>
      </footer>

      {/* Hidden input for iOS scanner capture (iPad BT HID requires focused input) */}
      <input
        ref={inputRef}
        type="text"
        inputMode="none"
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0.01 }}
      />

      {/* Scanner Active Indicator */}
      {isScanning && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
          <div className="bg-yellow-400/20 border-2 border-yellow-400 rounded-2xl px-8 py-6 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse" />
              <span className="text-yellow-400 font-bold text-lg">Scanning...</span>
            </div>
          </div>
        </div>
      )}

      {/* Scan Result Toast */}
      {scanToast && (
        <div className={clsx(
          'fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-medium transition-all',
          scanToast.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
        )}>
          {scanToast.message}
        </div>
      )}

    </div>
  );
}

export function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div
      className={clsx(
        'animate-spin rounded-full border-2 border-gray-600 border-t-yellow-400',
        sizeClasses[size]
      )}
    />
  );
}

export function LoadingPage() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export function Card({
  children,
  className = '',
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={clsx(
        'bg-gray-800 rounded-lg p-4 border border-gray-700',
        onClick && 'cursor-pointer hover:border-yellow-400',
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ready_to_start: 'bg-green-500 text-white',
    in_progress: 'bg-blue-500 text-white',
    coming_up: 'bg-gray-600 text-white',
    completed: 'bg-green-700 text-white',
    blocked: 'bg-red-500 text-white',
    skipped: 'bg-orange-500 text-white',
  };

  const labels: Record<string, string> = {
    ready_to_start: 'Ready',
    in_progress: 'In Progress',
    coming_up: 'Coming Up',
    completed: 'Completed',
    blocked: 'Blocked',
    skipped: 'Skipped',
  };

  return (
    <span className={clsx(
      'px-2 py-1 rounded text-xs font-medium',
      styles[status] || 'bg-gray-600 text-white'
    )}>
      {labels[status] || status}
    </span>
  );
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  className = '',
}: {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  const variantStyles = {
    primary: 'bg-yellow-400 text-gray-900 hover:bg-yellow-300',
    secondary: 'bg-gray-700 text-white hover:bg-gray-600',
    success: 'bg-green-600 text-white hover:bg-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-500',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'rounded-lg font-medium transition-colors min-h-[44px]',
        variantStyles[variant],
        sizeStyles[size],
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  );
}
